import { useState, useEffect } from 'react';
import { Check, ChevronsUpDown, Store, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useAuthStore } from '@/stores/auth.store';
import { useMerchants } from '@/features/merchants/hooks/use-merchants';
import type { MerchantWithStats } from '@/types/merchant';
import { cn } from '@/lib/utils';

export function MerchantSwitcher() {
  const [open, setOpen] = useState(false);
  const { user, currentMerchant, setCurrentMerchant, setGlobalView, isGlobalView } = useAuthStore();
  
  // Load merchants based on user permissions
  const { data: merchantData, isLoading } = useMerchants();
  
  // Extract merchants array from the response
  const merchants = merchantData?.merchants || [];
  
  // Filter merchants based on user role and permissions
  const availableMerchants = merchants.filter((merchant: MerchantWithStats) => {
    if (!user) return false;
    
    // BevPass admins can see all merchants
    if (user.role === 'bevpass_admin') {
      return true;
    }
    
    // Other users can only see their assigned merchants
    if (user.merchantIds?.includes(merchant.id)) {
      return true;
    }
    
    return false;
  });

  // Helper to resolve a merchant's best logo (branding first)
  const resolveMerchantLogo = (m: MerchantWithStats) => m.branding?.logoUrl || m.branding?.logoMarkUrl || m.logoUrl;

  // Auto-select first merchant if user only has access to one (and not a BevPass admin)
  useEffect(() => {
    if (!currentMerchant && availableMerchants.length === 1 && user?.role !== 'bevpass_admin') {
      const firstMerchant = availableMerchants[0];
      setCurrentMerchant({
        id: firstMerchant.id,
        name: firstMerchant.name,
        logoUrl: resolveMerchantLogo(firstMerchant)
      });
    }
  }, [availableMerchants, currentMerchant, setCurrentMerchant, user?.role]);

  const handleSelectGlobal = () => {
    setCurrentMerchant(null); // Clear current merchant
    setGlobalView(true);
    setOpen(false);
  };

  const handleSelectMerchant = (merchant: MerchantWithStats) => {
    setCurrentMerchant({
      id: merchant.id,
      name: merchant.name,
      logoUrl: resolveMerchantLogo(merchant)
    });
    setOpen(false);
  };

  // Don't show switcher if user has no merchant access
  if (!user || (!user.permissions?.canViewAllMerchants && !user.merchantIds?.length)) {
    return null;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('w-full justify-between shadow-sm hover:shadow-md')}
        >
          <div className="flex items-center gap-2">
            {!isGlobalView && currentMerchant ? (
              <>
                {currentMerchant.logoUrl ? (
                  <img
                    src={currentMerchant.logoUrl}
                    alt={currentMerchant.name}
                    className="h-5 w-5 rounded-sm object-cover border border-border/60"
                  />
                ) : (
                  <div className="p-1 rounded-md bg-primary/20 group-hover/trigger:bg-primary/30 transition-colors">
                    <Store className="h-3 w-3 text-primary" />
                  </div>
                )}
                <span className="truncate font-medium">{currentMerchant.name}</span>
              </>
            ) : (
              <>
                <div className="p-1 rounded-md bg-muted">
                  <Globe className="h-3 w-3 text-foreground" />
                </div>
                <span className="font-medium">All Merchants</span>
              </>
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={8} collisionPadding={8} className="w-[--radix-popover-trigger-width] p-2">
        <Command>
          <CommandInput 
            placeholder="Search merchants..." 
            className="border-none bg-transparent focus:ring-0 placeholder:text-muted-foreground/70"
          />
          <CommandList>
            <CommandEmpty className="text-muted-foreground py-6 text-center">No merchants found.</CommandEmpty>
            
            {/* Global view option for BevPass admins */}
            {user?.role === 'bevpass_admin' && (
              <>
                <CommandGroup heading="View" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  <CommandItem
                    value="global"
                    onSelect={handleSelectGlobal}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded-md transition-colors cursor-pointer",
                      isGlobalView ? "bg-muted" : "hover:bg-muted"
                    )}
                  >
                    <div className="p-1 rounded-md bg-muted">
                      <Globe className="h-3 w-3" />
                    </div>
                    <span className="flex-1 font-medium">All Merchants (Global View)</span>
                    {isGlobalView && <Check className="h-4 w-4" />}
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator className="my-2" />
              </>
            )}
            
            <CommandGroup heading="Merchants" className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {isLoading ? (
                <CommandItem disabled className="text-muted-foreground py-2">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-muted animate-pulse" />
                    Loading merchants...
                  </div>
                </CommandItem>
              ) : (
                availableMerchants.map((merchant: MerchantWithStats) => {
                  const isSelected = currentMerchant?.id === merchant.id && !isGlobalView;
                  return (
                    <CommandItem
                      key={merchant.id}
                      value={merchant.id}
                      onSelect={() => handleSelectMerchant(merchant)}
                    className={cn("flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer",
                      isSelected ? "bg-muted" : "hover:bg-muted")}
                  >
                    {resolveMerchantLogo(merchant) ? (
                      <img
                        src={resolveMerchantLogo(merchant) as string}
                        alt={merchant.name}
                        className="h-5 w-5 rounded-sm object-cover border border-border/60"
                      />
                    ) : (
                      <div className="p-1 rounded-md bg-muted">
                        <Store className="h-3 w-3" />
                      </div>
                    )}
                    <span className="truncate flex-1 font-medium">{merchant.name}</span>
                    {isSelected && <Check className="h-4 w-4" />}
                  </CommandItem>
                );
              })
            )}
          </CommandGroup>
        </CommandList>
      </Command>
      </PopoverContent>
    </Popover>
  );
}
